import React, { useEffect, useState } from 'react';
import { useVibrancy } from '../VibrancyDashboardContext';
import { VibrancyProfileService } from '../vibrancyProfileService';
import { BarRow, HeaderIcon, cardHeaderStyle, cardStyle } from './shared';

type Academic = NonNullable<ReturnType<typeof useVibrancy>['data']>['academic'];

// Fixed colour palette — applied by rank order
const BAR_COLORS = ['#1b52c4', '#0891b2', '#dc3545', '#f59e0b', '#7c3aed'];

// Default number of rows each college list shows
const COLLEGE_LIMIT = 5;

const normName = (s: string) => (s || '').trim().toLowerCase();

/** Drop the currently-selected institute from a college list (match by name), keep top N. */
const excludeSelf = <T extends { name: string }>(rows: T[], selfName: string, limit = COLLEGE_LIMIT): T[] => {
    const self = normName(selfName);
    return rows.filter(r => normName(r.name) !== self).slice(0, limit);
};

// ─── Sub-components (unchanged) ───────────────────────────────────────────────

const CollegeList: React.FC<{ title: string; rows: { name: string; count: number }[] }> = ({ title, rows }) => (
    <>
        <div className="text-uppercase fw-medium mb-2 font-14 text-secondary">{title}</div>
        <div className="d-flex flex-column gap-1 mb-3">
            {rows.map((r, i) => (
                <div key={i} className="d-flex justify-content-between font-13">
                    <span>{r.name}</span><span className="fw-bold">{r.count}</span>
                </div>
            ))}
        </div>
    </>
);

const TopCollegesCard: React.FC<{ instituteId: string | number; showUg: boolean }> = ({ instituteId, showUg }) => {
    const { selectedInstituteName } = useVibrancy();
    const [ugIndian, setUgIndian] = useState<{ name: string; alumniCount: number }[]>([]);
    const [pgIndian, setPgIndian] = useState<{ name: string; alumniCount: number }[]>([]);
    const [loading,  setLoading]  = useState(false);

    useEffect(() => {
        setLoading(true);
        // Fetch one extra so the list still shows COLLEGE_LIMIT rows after excluding self
        VibrancyProfileService.getTopColleges(instituteId, COLLEGE_LIMIT + 1)
            .then(r => {
                setUgIndian(r.data?.data?.ugIndian || []);
                setPgIndian(r.data?.data?.pgIndian || []);
            })
            .catch(() => { setUgIndian([]); setPgIndian([]); })
            .finally(() => setLoading(false));
    }, [instituteId]);

    const ugRows = excludeSelf(ugIndian, selectedInstituteName);
    const pgRows = excludeSelf(pgIndian, selectedInstituteName);

    return (
        <div className="card h-100" style={cardStyle}>
            <div className="card-header bg-white d-flex justify-content-between align-items-center" style={cardHeaderStyle}>
                <div className="fw-medium d-flex align-items-center gap-2 font-14"><HeaderIcon icon="🏛️" bg="#fef3c7" color="#92400e" />Top Colleges</div>
                <span className="font-13 text-secondary">Destinations</span>
            </div>
            <div className="card-body py-2">
                {loading ? (
                    <div className="placeholder-glow d-flex flex-column gap-2">
                        {[1,2,3,4,5].map(i => <span key={i} className="placeholder col-12 rounded" style={{ height: 14 }} />)}
                    </div>
                ) : (
                    <>
                        {showUg && <CollegeList title="Top UG Indian Colleges" rows={ugRows.map(r => ({ name: r.name, count: r.alumniCount }))} />}
                        <CollegeList title="Top PG Indian Colleges" rows={pgRows.map(r => ({ name: r.name, count: r.alumniCount }))} />
                    </>
                )}
            </div>
        </div>
    );
};

const IntlCard: React.FC<{ instituteId: string | number; showUg: boolean }> = ({ instituteId, showUg }) => {
    const { selectedInstituteName } = useVibrancy();
    const [ugAbroad, setUgAbroad] = useState<{ name: string; alumniCount: number }[]>([]);
    const [pgAbroad, setPgAbroad] = useState<{ name: string; alumniCount: number }[]>([]);
    const [loading,  setLoading]  = useState(false);

    useEffect(() => {
        setLoading(true);
        // Fetch one extra so the list still shows COLLEGE_LIMIT rows after excluding self
        VibrancyProfileService.getTopIntlColleges(instituteId, COLLEGE_LIMIT + 1)
            .then(r => {
                setUgAbroad(r.data?.data?.ugAbroad || []);
                setPgAbroad(r.data?.data?.pgAbroad || []);
            })
            .catch(() => { setUgAbroad([]); setPgAbroad([]); })
            .finally(() => setLoading(false));
    }, [instituteId]);

    const ugRows = excludeSelf(ugAbroad, selectedInstituteName);
    const pgRows = excludeSelf(pgAbroad, selectedInstituteName);

    return (
        <div className="card h-100" style={cardStyle}>
            <div className="card-header bg-white" style={cardHeaderStyle}>
                <div className="fw-medium d-flex align-items-center gap-2 font-14">
                    <HeaderIcon icon="✈️" bg="#cffafe" color="#0891b2" />Top International Colleges
                </div>
            </div>
            <div className="card-body py-2">
                {loading ? (
                    <div className="placeholder-glow d-flex flex-column gap-2">
                        {[1,2,3,4,5].map(i => <span key={i} className="placeholder col-12 rounded" style={{ height: 14 }} />)}
                    </div>
                ) : (
                    <>
                        {showUg && <CollegeList title="UG Abroad" rows={ugRows.map(r => ({ name: r.name, count: r.alumniCount }))} />}
                        <CollegeList title="PG Abroad" rows={pgRows.map(r => ({ name: r.name, count: r.alumniCount }))} />
                    </>
                )}
            </div>
        </div>
    );
};

const PrestigiousCard: React.FC<{
    count: number; loading: boolean;
    list: { instituteName: string; alumniCount: number; width: number }[];
}> = ({ count, loading, list }) => (
    <div className="card h-100" style={cardStyle}>
        <div className="card-header bg-white d-flex justify-content-between align-items-center" style={cardHeaderStyle}>
            <div className="fw-medium d-flex align-items-center gap-2 font-14">
                <HeaderIcon icon="🏆" bg="#fef3c7" color="#92400e" />Prestigious Institutions
            </div>
            <span className="font-13 text-secondary">IIT · IIM · Ivy · Oxbridge</span>
        </div>
        <div className="card-body">
            {loading ? (
                <div className="placeholder-glow text-center">
                    <span className="placeholder col-6 d-block mx-auto mb-2" style={{ height: 42 }} />
                    <span className="placeholder col-8 d-block mx-auto mb-3" style={{ height: 12 }} />
                    {[1,2,3].map(i => <span key={i} className="placeholder col-12 d-block rounded mb-2" style={{ height: 14 }} />)}
                </div>
            ) : (
                <>
                    <div className="text-center mb-3">
                        <div className="fw-bold" style={{ fontSize: 42, color: '#0a1f44', lineHeight: 1 }}>{count}</div>
                        <div className="fw-semibold mt-2" style={{ fontSize: 11, color: '#8fa0c0' }}>alumni at top-tier institutions</div>
                    </div>
                    {list.map((r, i) => (
                        <BarRow key={i} label={r.instituteName} right={r.alumniCount} color="#92400e" width={r.width} />
                    ))}
                </>
            )}
        </div>
    </div>
);

const ScholarshipsCard: React.FC<{
    count: number; loading: boolean;
    list: { name: string; passOutYear: number | null; level: string; scholarshipType: string | null; collegeName: string | null }[];
}> = ({ count, loading, list }) => (
    <div className="card h-100" style={cardStyle}>
        <div className="card-header bg-white d-flex justify-content-between align-items-center" style={cardHeaderStyle}>
            <div className="fw-medium d-flex align-items-center gap-2 font-14">
                <HeaderIcon icon="🎁" bg="#d1fae5" color="#0f9d58" />Scholarship Recipients
            </div>
            <span className="font-13 text-secondary">K-12 alumni only</span>
        </div>
        <div className="card-body">
            {loading ? (
                <div className="placeholder-glow text-center">
                    <span className="placeholder col-6 d-block mx-auto mb-2" style={{ height: 42 }} />
                    <span className="placeholder col-8 d-block mx-auto mb-3" style={{ height: 12 }} />
                    {[1,2,3].map(i => <span key={i} className="placeholder col-12 d-block rounded mb-2" style={{ height: 14 }} />)}
                </div>
            ) : (
                <>
                    <div className="text-center mb-3">
                        <div className="fw-bold" style={{ fontSize: 42, color: '#0f9d58', lineHeight: 1 }}>{count}</div>
                        <div className="fw-semibold mt-2" style={{ fontSize: 11, color: '#8fa0c0' }}>scholarship recipients on record</div>
                    </div>
                    {list.map((r, i) => (
                        <div key={i} className="d-flex justify-content-between align-items-start py-1 border-bottom"
                            style={{ borderColor: '#f0f4fa', fontSize: 12 }}>
                            <div>
                                <div className="fw-medium" style={{ color: '#0a1f44' }}>{r.name}</div>
                                {r.collegeName && <div className="text-secondary" style={{ fontSize: 11 }}>{r.collegeName}</div>}
                            </div>
                            <div className="text-end ms-2 flex-shrink-0">
                                {r.scholarshipType && (
                                    <span className="badge rounded-pill" style={{ fontSize: 9, background: '#d1fae5', color: '#065f46' }}>
                                        {r.scholarshipType}
                                    </span>
                                )}
                                <div className="text-secondary mt-1" style={{ fontSize: 10 }}>{r.level}{r.passOutYear ? ` · ${r.passOutYear}` : ''}</div>
                            </div>
                        </div>
                    ))}
                </>
            )}
        </div>
    </div>
);

const PgResearchCard: React.FC<{ a: Academic }> = ({ a }) => (
    <div className="card h-100" style={cardStyle}>
        <div className="card-header bg-white d-flex justify-content-between align-items-center" style={cardHeaderStyle}>
            <div className="fw-medium d-flex align-items-center gap-2 font-14"><HeaderIcon icon="🔬" bg="#ede9fe" color="#7c3aed" />PG & Research Admissions</div>
            <span className="font-13 text-secondary">Postgraduate footprint</span>
        </div>
        <div className="card-body">
            <div className="text-center mb-3">
                <div className="fw-bold" style={{ fontSize: 42, color: '#7c3aed', lineHeight: 1 }}>{a.pgResearch.total}</div>
                <div className="fw-semibold mt-1" style={{ fontSize: 11, color: '#8fa0c0' }}>alumni in PG / research programs</div>
            </div>
            {a.pgResearch.breakdown.map((b, i) => <BarRow key={i} label={b.label} right={b.count} color={b.color} width={b.width} />)}
        </div>
    </div>
);

// ─── UgCoursesCard — live API ─────────────────────────────────────────────────

const UgCoursesCard: React.FC<{ instituteId: string | number }> = ({ instituteId }) => {
    const [courses, setCourses] = useState<{ label: string; pct: number; width: number }[]>([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        setLoading(true);
        VibrancyProfileService.getTopUgCourses(instituteId)
            .then(r => setCourses(r.data?.data || []))
            .catch(() => setCourses([]))
            .finally(() => setLoading(false));
    }, [instituteId]);

    return (
        <div className="card h-100" style={cardStyle}>
            <div className="card-header bg-white d-flex justify-content-between align-items-center" style={cardHeaderStyle}>
                <div className="fw-medium d-flex align-items-center gap-2 font-14">
                    <HeaderIcon icon="📚" bg="#ede9fe" color="#7c3aed" />Top UG Courses Pursued
                </div>
                <span className="font-13 text-secondary">By % of alumni</span>
            </div>
            <div className="card-body">
                {loading ? (
                    <div className="placeholder-glow d-flex flex-column gap-3">
                        {[1, 2, 3, 4, 5].map(i => (
                            <span key={i} className="placeholder col-12 rounded" style={{ height: 14 }} />
                        ))}
                    </div>
                ) : courses.length === 0 ? (
                    <div className="text-muted small text-center py-3">No course data available</div>
                ) : (
                    courses.map((c, i) => (
                        <BarRow
                            key={i}
                            label={c.label}
                            right={`${c.pct}%`}
                            color={BAR_COLORS[i % BAR_COLORS.length]}
                            width={c.width}
                        />
                    ))
                )}
            </div>
        </div>
    );
};

// ─── Main component (layout logic unchanged) ──────────────────────────────────

/** VD-08 Academic Footprint — renders dynamically by institute type, grid reflows. */
const AcademicFootprint: React.FC = () => {
    const { data, selectedInstituteId } = useVibrancy();

    const [prestigious,     setPrestigious]     = useState(0);
    const [scholarships,    setScholarships]    = useState(0);
    const [prestigiousList, setPrestigiousList] = useState<any[]>([]);
    const [scholarshipList, setScholarshipList] = useState<any[]>([]);
    const [countsLoading,   setCountsLoading]   = useState(false);

    useEffect(() => {
        if (!selectedInstituteId) return;
        setCountsLoading(true);
        VibrancyProfileService.getAcademicCounts(selectedInstituteId)
            .then(r => {
                const d = r.data?.data;
                setPrestigious(d?.prestigious   || 0);
                setScholarships(d?.scholarships || 0);
                setPrestigiousList(d?.prestigiousList || []);
                setScholarshipList(d?.scholarshipList || []);
            })
            .catch(() => { setPrestigious(0); setScholarships(0); setPrestigiousList([]); setScholarshipList([]); })
            .finally(() => setCountsLoading(false));
    }, [selectedInstituteId]);

    if (!data || !selectedInstituteId) return null;
    const a    = data.academic;
    const type = data.institute.type;

    if (type === 'college_pg') {
        return <div className="row g-3 mb-3"><div className="col-md-12"><PgResearchCard a={a} /></div></div>;
    }

    if (type === 'college_ug') {
        return (
            <>
                <div className="row g-3 mb-3">
                    <div className="col-md-4 col-sm-6"><TopCollegesCard instituteId={selectedInstituteId} showUg={false} /></div>
                
                    <div className="col-md-4 col-sm-6"><IntlCard instituteId={selectedInstituteId} showUg={false} /></div>
                    <div className="col-md-4 col-sm-6"><PrestigiousCard count={prestigious} loading={countsLoading} list={prestigiousList} /></div>
                </div>
            </>
        );
    }

    // K-12 — full footprint
    return (
        <>
            <div className="row g-3 mb-3">
                <div className="col-md-6"><UgCoursesCard instituteId={selectedInstituteId} /></div>
                <div className="col-md-6"><TopCollegesCard instituteId={selectedInstituteId} showUg={true} /></div>
            </div>
            <div className="row g-3 mb-3">
                <div className="col-md-4"><IntlCard instituteId={selectedInstituteId} showUg={true} /></div>
                <div className="col-md-4"><PrestigiousCard count={prestigious} loading={countsLoading} list={prestigiousList} /></div>
                <div className="col-md-4"><ScholarshipsCard count={scholarships} loading={countsLoading} list={scholarshipList} /></div>
            </div>
        </>
    );
};

export default AcademicFootprint;
